/**
 * Cloudflare Turnstile widget. Uses the backend's /api/public/forms/turnstile-status
 * endpoint to discover (a) whether Turnstile is configured server-side and (b)
 * the site key. Calls `onToken(token)` when the widget completes.
 *
 * If Turnstile is NOT configured server-side, the widget renders nothing and the
 * parent form will submit without a token. The backend will accept the submission
 * (graceful no-op).
 */
import React, { useEffect, useRef, useState } from "react";
import { publicApi } from "@/lib/api";

let scriptInjected = false;
function ensureScript() {
  if (scriptInjected) return;
  if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]')) {
    scriptInjected = true;
    return;
  }
  const s = document.createElement("script");
  s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
  scriptInjected = true;
}

let statusCache = null;
async function loadStatus() {
  if (statusCache) return statusCache;
  try {
    const r = await publicApi.get("/public/forms/turnstile-status");
    statusCache = r.data;
  } catch {
    statusCache = { configured: false };
  }
  return statusCache;
}

export default function TurnstileWidget({ onToken, theme = "auto" }) {
  const elRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadStatus().then((s) => { if (!cancelled) setStatus(s); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!status?.configured || !status.site_key) return;
    ensureScript();
    let attempts = 0;
    const tryRender = () => {
      if (!elRef.current) return;
      if (!window.turnstile || !window.turnstile.render) {
        if (attempts++ < 50) setTimeout(tryRender, 100);
        return;
      }
      if (widgetIdRef.current) return; // already rendered
      widgetIdRef.current = window.turnstile.render(elRef.current, {
        sitekey: status.site_key,
        theme,
        callback: (token) => onToken && onToken(token),
        "error-callback": () => onToken && onToken(""),
        "expired-callback": () => onToken && onToken(""),
      });
    };
    tryRender();
    return () => {
      if (widgetIdRef.current && window.turnstile?.remove) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [status, theme, onToken]);

  if (status === null) return null; // loading
  if (!status.configured) return null; // Turnstile not configured — render nothing
  return (
    <div className="mt-2" data-testid="turnstile-widget">
      <div ref={elRef} />
    </div>
  );
}
